import { isErrorWithData } from '@/services/helpers';
import { useUpdateSalesRepMutation } from '@/services/salesReps';
import { Badge, Box, Button, Modal, SelectInput, TextareaInput, toast } from '@palmetto/palmetto-components';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  SalesRep,
  SalesRepStatus,
  salesRepStatusLabels,
  salesRepStatusModalHelperText,
  salesRepStatusModalSelectableStatuses,
} from 'types';

import { salesRepStatusBadgeVariant } from './statusBadge';

type ChangeSalesRepStatusModalProps = {
  isOpen: boolean;
  salesRep: SalesRep | undefined;
  onClose: () => void;
};

interface FormValues {
  status: SalesRepStatus | '';
  reason: string;
}

type StatusOption = { value: SalesRepStatus; label: string };

export const ChangeSalesRepStatusModal = ({ isOpen, salesRep, onClose }: ChangeSalesRepStatusModalProps) => {
  const [updateSalesRep, { isLoading }] = useUpdateSalesRepMutation();

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: { status: '', reason: '' },
  });

  const currentStatus = salesRep?.status;

  const statusOptions = useMemo<StatusOption[]>(
    () =>
      salesRepStatusModalSelectableStatuses
        .filter((s) => s !== currentStatus)
        .map((s) => ({ value: s, label: salesRepStatusLabels[s] })),
    [currentStatus],
  );

  const watchedStatus = watch('status');
  const helperText = watchedStatus ? salesRepStatusModalHelperText[watchedStatus] : undefined;

  const handleClose = useCallback(() => {
    reset({ status: '', reason: '' });
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!salesRep || !values.status) return;
      try {
        await updateSalesRep({
          id: salesRep.id,
          status: values.status,
          reason: values.reason,
        }).unwrap();
        toast.success('Sales rep status updated');
        handleClose();
      } catch (error) {
        console.error(error);
        if (isErrorWithData(error)) {
          toast.error(error.data.message);
        } else {
          toast.error('Failed to update sales rep status. Please try again.');
        }
      }
    },
    [salesRep, updateSalesRep, handleClose],
  );

  if (!isOpen || !salesRep) return null;

  return (
    <Modal ariaLabelledBy="changeSalesRepStatusTitle" isOpen={isOpen} onDismiss={handleClose} maxWidth="5xl">
      <Modal.Header id="changeSalesRepStatusTitle" title="Change Sales Rep Status" onDismiss={handleClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body background="secondary" childGap="lg">
          <Box gap="xs">
            <Box display="flex" direction="row" gap="xs">
              <Box fontWeight="medium">Name:</Box> {salesRep.idProviderUser?.displayName}
            </Box>
            <Box display="flex" direction="row" gap="xs">
              <Box fontWeight="medium">Email:</Box> {salesRep.idProviderUser?.emails?.[0] ?? '—'}
            </Box>
            <Box display="flex" direction="row" gap="xs" alignItems="center">
              <Box fontWeight="medium">Current Status:</Box>
              {currentStatus ? (
                <Badge
                  variant={salesRepStatusBadgeVariant[currentStatus] ?? 'default'}
                  message={salesRepStatusLabels[currentStatus]}
                />
              ) : (
                '—'
              )}
            </Box>
          </Box>
          <Controller
            name="status"
            control={control}
            rules={{ required: 'Status is required' }}
            render={({ field, fieldState }) => (
              <SelectInput
                label="New Status"
                name={field.name}
                id="changeSalesRepStatus-status"
                value={statusOptions.find((o) => o.value === field.value) || null}
                onChange={(event) => field.onChange(event.target.value?.value || '')}
                onBlur={field.onBlur}
                options={statusOptions}
                error={fieldState.error?.message}
                helpText={helperText}
                isRequired
              />
            )}
          />
          <Controller
            name="reason"
            control={control}
            rules={{
              required: 'Reason is required',
              minLength: { value: 1, message: 'Reason is required' },
            }}
            render={({ field, fieldState }) => (
              <TextareaInput
                label="Reason"
                name={field.name}
                id="changeSalesRepStatus-reason"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                rows={3}
                error={fieldState.error?.message}
                isRequired
              />
            )}
          />
        </Modal.Body>
        <Modal.Footer justifyContent="space-between">
          <Button onClick={handleClose} variant="secondary" tone="neutral" isDisabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading} isDisabled={isLoading}>
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
